import React, { EventHandler, useCallback, useEffect, useState } from 'react';
import { Box, Flex, Text } from 'rebass';
import PropTypes from 'prop-types';

import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Input, Label } from '../Form';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import WaterSupplierPicker, {
  FETCH_WATER_SUPPLIERS,
  WaterSupplier,
} from '../WaterSupplier/WaterSupplierPicker';
import DataTable from '../Table/DataTable';
import { Column } from '../../hooks/useTablePages';
import CustomerPicker from '../Customer/CustomerPicker';
import TesterPicker, { Tester } from '../Tester/TesterPicker';
import useNotify from '../../hooks/useNotify';
import { useHistory } from 'react-router-dom';
import useMode from '../../hooks/useMode';
import Form from '../Form/Form';
import FormColumn from '../Form/FormColumn';
import FormRow from '../Form/FormRow';
import ButtonToggle from '../Button/ButtonToggle';
import AddressPicker from '../Address/AddressPicker';
import AssemblyEditModal from '../BackflowAssembly/AssemblyEditModal';
import { PortalWithState } from 'react-portal';
import Address from '../Address/Address';
import { gql as company_gql } from '@/modes/company/gql';
import { gql as waterSupplier_gql } from '@/modes/waterSupplier/gql';
import { UserMode } from '@/hooks/useApolloClient';
import WaterServicePicker from '../WaterService/WaterServicePicker';
import useSessionInfo from '@/hooks/useSessionInfo';

const assemblyColumns: Column[] = [
  {
    header: 'Model',
    id: 'model',
    accessor: (assembly: any) => {
      return `${assembly.model.backflowAssemblyMake.name} ${assembly.model.name}`;
    },
    width: 180,
  },
  {
    header: 'Serial',
    accessor: 'serialNumber',
    width: 100,
  },
  {
    header: 'Type',
    accessor: 'type',
    width: 70,
  },
  {
    header: 'Size',
    id: 'size',
    accessor: (assembly: any): string =>
      assembly.size ? assembly.size + '"' : '',
    width: 80,
  },
  {
    header: 'Location',
    accessor: 'location',
    width: 288,
  },
];

const FETCH_SERVICES = company_gql(`#graphql
  query GetWaterServices($where: JSON!) {
    waterServices(
      where: $where
      orderBy: [{ col: "address.street1", dir: ASC }]
      limit: null
    ) {
      id
      addressId
      address(required: true) {
        id
        street1
        street2
        city
        state
        zip
      }
      backflowAssemblies(where: { status: 1 }, required: true) {
        id
        type
        serialNumber
        size
        location
        model {
          name
          backflowAssemblyMake {
            name
          }
        }
      }
    }
  }
`);

const FETCH_ASSEMBLIES = company_gql(`#graphql
  query GetAssemblies($where: JSON!) {
    backflowAssemblies(
      where: $where
      orderBy: [{ col: "address.street1", dir: ASC }]
      limit: null
    ) {
      id
      type
      serialNumber
      size
      location
      model {
        name
        backflowAssemblyMake {
          name
        }
      }
    }
  }
`);

type BackflowAssembly = {
  id: string;
  type: string;
  serialNumber: string;
  size: string;
};

type WaterService = {
  id: string;
  siteNumber: string;
  meterNumber: string;
  addressId: string;
  address: {
    id: string;
    street1: string;
    street2: string;
    city: string;
    state: string;
    zip: string;
  };
  backflowAssemblies: BackflowAssembly[];
};

const CREATE_JOB_COMPANY = company_gql(`#graphql
  mutation CreateJob($input: JobInput!) {
    createJob(input: $input) {
      id
    }
  }
`);

const CREATE_JOB_WATER_SUPPLIER = waterSupplier_gql(`#graphql
  mutation CreateJob($input: JobInput!) {
    createJob(input: $input) {
      id
    }
  }
`);
type BillingStatusKey = 'NOT_PAID' | 'PAID' | 'NOT_BILLABLE';

export type InboundAssignmentInput = {
  customerProfileId: string;
  due?: number;
  priceListId?: string;
  billingStatus?: BillingStatusKey;
};

export type OutboundAssignmentInput = {
  vendorProfileId: string;
  due?: number;
};

export type BackflowTestInput = {
  waterSupplierId?: string;
  backflowAssemblyId?: string;
};

export type JobTypeKey =
  | 'BACKFLOW_TEST'
  | 'NEW_INSTALL'
  | 'DISCRETIONARY'
  | 'COMPLIANCE'
  | 'RESEARCH'
  | 'ADMINISTRATIVE'
  | 'RW_INSPECTION';

export type JobInput = {
  addressId: string;
  type: JobTypeKey;
  backflowTest?: BackflowTestInput;
  inboundAssignment: InboundAssignmentInput;
  outboundAssignment?: OutboundAssignmentInput;
};

const useLastWaterSupplierId = () => {
  const mode = useMode();
  const storageKey = `${mode}.lastWaterSupplierId`;

  const [waterSupplierId, setWaterSupplierId] = useState<string | undefined>(
    localStorage.getItem(storageKey) ?? undefined
  );

  const sessionInfo = useSessionInfo();

  useEffect(() => {
    if (storageKey && waterSupplierId) {
      localStorage.setItem(storageKey, waterSupplierId);
    }
  }, [waterSupplierId, storageKey]);

  return {
    waterSupplierId:
      mode === 'water-supplier'
        ? sessionInfo?.session?.companyProfile?.waterSupplier?.profileId
        : waterSupplierId,
    setWaterSupplierId,
  };
};

type AddJobTracModalProps = {
  onClose: () => void;
};

const AddJobModal: React.FC<AddJobTracModalProps> = ({
  onClose,
}: AddJobTracModalProps) => {
  const { waterSupplierId, setWaterSupplierId } = useLastWaterSupplierId();
  const [lookupMethod, setLookupMethod] = useState<'trac' | 'serial'>('trac');
  const [customerProfileId, setCustomerProfileId] = useState<string>();
  const [tester, setTester] = useState<Tester | null>(null);
  const [nestedTester, setNestedTester] = useState<Tester | null>(null);
  const testerProfileId = tester?.profileId;
  const [trac, setTrac] = useState<string>();
  const [serial, setSerial] = useState<string>();
  const [waterServiceId, setWaterServiceId] = useState<string>();
  const [addressId, setAddressId] = useState<string>();
  const [selectedAssemblyIds, setSelectedAssemblyIds] = useState(
    [] as readonly string[]
  );
  const { showError } = useNotify();
  const history = useHistory();
  const mode: UserMode = useMode();

  const lookupMethodChangeHandler = useCallback<EventHandler<any>>(
    (e) => {
      setLookupMethod(e.target.value);
    },
    [setLookupMethod]
  );

  const lookupWhere =
    mode === 'water-supplier'
      ? waterServiceId
        ? { id: waterServiceId }
        : undefined
      : lookupMethod === 'trac' && trac && trac.length > 4
        ? { pin: trac }
        : lookupMethod === 'serial' && addressId && serial && serial.length > 4
          ? { addressId, ['backflowAssemblies.serialNumber']: serial }
          : undefined;

  const { data: waterSuppliersData } = useQuery<{
    waterSuppliers: WaterSupplier[];
  }>(FETCH_WATER_SUPPLIERS, {
    variables: {
      where: {
        profileId: waterSupplierId,
      },
    },
    fetchPolicy: 'network-only',
    skip: !waterSupplierId,
  });

  const waterSupplier: WaterSupplier | undefined =
    waterSuppliersData?.waterSuppliers[0];

  const { data: waterServicesData } = useQuery<{
    waterServices: WaterService[];
  }>(FETCH_SERVICES, {
    variables: {
      where: {
        waterSupplierId,
        ...lookupWhere,
      },
    },
    onCompleted: (waterServicesData) => {
      if (waterServicesData?.waterServices?.[0]?.backflowAssemblies) {
        //if lookup method is serial, only display the assembly with the matching serial number as selected
        const assemblies =
          lookupMethod === 'serial'
            ? waterServicesData.waterServices[0].backflowAssemblies.filter(
                (assembly) => assembly.serialNumber == serial
              )
            : waterServicesData.waterServices[0].backflowAssemblies;

        setSelectedAssemblyIds(assemblies.map((assembly) => assembly.id));
      }
    },
    fetchPolicy: 'network-only',
    skip:
      !waterSupplierId ||
      !lookupWhere ||
      (mode !== 'water-supplier' && !waterSupplier?.hasUser),
  });

  const { data: backflowAssembliesData, refetch: refetchAssemblies } =
    useQuery<{
      backflowAssemblies: BackflowAssembly[];
    }>(FETCH_ASSEMBLIES, {
      variables: {
        where: {
          status: 1,
          waterSupplierId,
          addressId,
        },
      },
      onCompleted: (backflowAssembliesData) => {
        if (backflowAssembliesData?.backflowAssemblies) {
          setSelectedAssemblyIds(
            backflowAssembliesData?.backflowAssemblies.map(
              (assembly) => assembly.id
            )
          );
        }
      },
      fetchPolicy: 'network-only',
      skip:
        !waterSupplierId ||
        !addressId ||
        (mode !== 'water-supplier' && waterSupplier?.hasUser),
    });

  const backflowAssemblies =
    mode === 'water-supplier' || waterSupplier?.hasUser
      ? waterServicesData?.waterServices[0]?.backflowAssemblies
      : backflowAssembliesData?.backflowAssemblies;

  const serviceAddressId =
    mode === 'water-supplier' || waterSupplier?.hasUser
      ? waterServicesData?.waterServices?.[0]?.addressId
      : addressId;

  const [createJobCompany] = useMutation(CREATE_JOB_COMPANY);
  const [createJobWaterSupplier] = useMutation(CREATE_JOB_WATER_SUPPLIER);

  const save = async () => {
    if (!serviceAddressId || !customerProfileId) {
      return;
    }

    const assignmentIds: string[] = [];
    try {
      if (selectedAssemblyIds.length) {
        for (const backflowAssemblyId of selectedAssemblyIds) {
          let result;
          if (mode === 'company') {
            result = await createJobCompany({
              variables: {
                input: {
                  addressId: serviceAddressId,
                  type: 'BACKFLOW_TEST',
                  backflowTest: {
                    backflowAssemblyId,
                    waterSupplierId,
                  },
                  inboundAssignment: {
                    customerProfileId,
                  },
                  outboundAssignment: testerProfileId
                    ? {
                        vendorProfileId: testerProfileId,
                      }
                    : undefined,
                },
              },
            });
          } else if (mode === 'water-supplier') {
            result = await createJobWaterSupplier({
              variables: {
                input: {
                  addressId: serviceAddressId,
                  type: 'BACKFLOW_TEST',
                  backflowTest: {
                    backflowAssemblyId,
                    waterSupplierId,
                  },
                  inboundAssignment: {
                    customerProfileId,
                  },
                  outboundAssignment: tester?.profileId
                    ? {
                        vendorProfileId: tester?.profileId,
                        nestedAssignmentProfileId: nestedTester?.profileId,
                      }
                    : undefined,
                },
              },
            });
          }
          if (result?.data?.createJob?.id) {
            assignmentIds.push(result.data.createJob.id);
          }
        }
      } else if (!waterSupplier?.hasUser) {
        const result = await createJobCompany({
          variables: {
            input: {
              addressId: serviceAddressId,
              type: 'BACKFLOW_TEST',
              backflowTest: {
                waterSupplierId,
              },
              inboundAssignment: {
                customerProfileId,
              },
              outboundAssignment: testerProfileId
                ? {
                    vendorProfileId: testerProfileId,
                  }
                : undefined,
            },
          },
        });
        if (result?.data?.createJob?.id) {
          assignmentIds.push(result.data.createJob.id);
        }
      }

      if (assignmentIds.length > 0) {
        history.push(`/${mode}/jobs/${assignmentIds[0]}`);
      }
      onClose();
    } catch (e) {
      showError('Error creating job', e as ApolloError);
    }
  };

  const canAssignNestedTester =
    mode === 'water-supplier' && tester?.profile?.type === 'COMPANY';

  return (
    <Modal title="Add Backflow Test" onClose={onClose}>
      <ModalBody>
        <Form>
          {mode !== 'water-supplier' && (
            <FormColumn>
              <Label>Water Supplier</Label>
              <WaterSupplierPicker
                waterSupplierId={waterSupplierId}
                onChange={(waterSupplier) =>
                  setWaterSupplierId(waterSupplier?.profileId)
                }
              />
            </FormColumn>
          )}
          {mode !== 'water-supplier' &&
            waterSupplier &&
            waterSupplier.hasUser && (
              <>
                <FormColumn>
                  <Label>Lookup By</Label>
                  <ButtonToggle
                    onClick={lookupMethodChangeHandler}
                    value={lookupMethod}
                    name="lookupMethod"
                    buttonOptions={[
                      { label: 'TRAC', value: 'trac' },
                      { label: 'Serial #', value: 'serial' },
                    ]}
                  />
                </FormColumn>
                {lookupMethod === 'trac' && (
                  <FormRow>
                    <FormColumn>
                      <Label>TRAC</Label>
                      <Input
                        value={trac}
                        autoFocus
                        onChange={(e) => setTrac(e.target.value)}
                        autoComplete="off"
                      />
                    </FormColumn>
                    {waterServicesData?.waterServices?.[0] && (
                      <FormColumn>
                        <Label>Service Address</Label>
                        <Address
                          address={
                            waterServicesData?.waterServices?.[0].address
                          }
                        />
                      </FormColumn>
                    )}
                  </FormRow>
                )}
                {lookupMethod === 'serial' && (
                  <FormRow>
                    <FormColumn>
                      <Label>Service Address</Label>
                      <AddressPicker
                        addressId={addressId}
                        onChange={(address) => setAddressId(address?.id)}
                        autoFocus
                      />
                    </FormColumn>
                    <FormColumn>
                      <Label>Serial #</Label>
                      <Input
                        value={serial}
                        onChange={(e) => setSerial(e.target.value)}
                        autoComplete="off"
                      />
                    </FormColumn>
                  </FormRow>
                )}
              </>
            )}
          {mode === 'water-supplier' && (
            <FormColumn>
              <Label>Water Service</Label>
              <WaterServicePicker
                waterServiceId={waterServiceId}
                onChange={(waterService) => setWaterServiceId(waterService.id)}
              />
            </FormColumn>
          )}
          {mode !== 'water-supplier' &&
            waterSupplier &&
            !waterSupplier.hasUser && (
              <>
                <FormRow>
                  <FormColumn>
                    <Label>Address</Label>
                    <AddressPicker
                      addressId={addressId}
                      onChange={(address) => setAddressId(address?.id)}
                      autoFocus
                      allowCreate
                    />
                  </FormColumn>
                </FormRow>
              </>
            )}
          {waterSupplier && backflowAssemblies && (
            <>
              <FormColumn>
                <Flex alignItems="center">
                  <Label>Assemblies</Label>
                  {!waterSupplier.hasUser && (
                    <PortalWithState closeOnEsc>
                      {({ openPortal, closePortal, portal }) => (
                        <>
                          <Button
                            variant="primary"
                            icon="plus"
                            onClick={openPortal}
                            ml={1}
                          >
                            Assembly
                          </Button>
                          {portal(
                            <AssemblyEditModal
                              action="create"
                              assembly={{
                                addressId: serviceAddressId,
                                waterSupplierId,
                              }}
                              onClose={(assemblyId) => {
                                if (assemblyId) {
                                  setSelectedAssemblyIds((oldAssemblyIds) => {
                                    return [...oldAssemblyIds, assemblyId];
                                  });
                                }
                                closePortal();
                                refetchAssemblies();
                              }}
                            />
                          )}
                        </>
                      )}
                    </PortalWithState>
                  )}
                </Flex>
                {backflowAssemblies.length > 0 && (
                  <Flex flexShrink={1} flexGrow={1} maxHeight={200}>
                    <DataTable
                      columns={assemblyColumns}
                      selectedIds={selectedAssemblyIds}
                      onSelectionChange={setSelectedAssemblyIds}
                      allowSelection
                      items={backflowAssemblies}
                    />
                  </Flex>
                )}
                {!backflowAssemblies.length && (
                  <Text variant="body2" color="fieldBorderHover">
                    {waterSupplier.profile.name} has no assemblies at this
                    address
                  </Text>
                )}
              </FormColumn>
              <FormRow>
                <FormColumn>
                  <Label>Customer</Label>
                  <CustomerPicker
                    customerProfileId={customerProfileId}
                    allowCreate
                    isClearable
                    serviceAddressId={serviceAddressId}
                    serviceAddressWhere={{
                      active: true,
                      addressId: serviceAddressId,
                    }}
                    where={{
                      status: 'ACTIVE',
                    }}
                    onChange={(customer) =>
                      setCustomerProfileId(customer?.profileId)
                    }
                  />
                </FormColumn>

                <FormColumn>
                  <Label>Tester</Label>
                  <TesterPicker
                    testerProfileId={testerProfileId}
                    isClearable
                    where={{
                      status: ['ACTIVE', 'PENDING'],
                    }}
                    onChange={(tester) => setTester(tester)}
                  />
                  {canAssignNestedTester && (
                    <Box mt={2}>
                      <TesterPicker
                        testerProfileId={nestedTester?.profileId}
                        companyProfileId={tester?.profileId}
                        onChange={setNestedTester}
                      />
                    </Box>
                  )}
                </FormColumn>
              </FormRow>
            </>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          icon="save"
          onClick={save}
          disabled={
            !waterSupplier ||
            (waterSupplier.hasUser &&
              (!selectedAssemblyIds || selectedAssemblyIds.length === 0)) ||
            !customerProfileId
          }
        >
          Add{' '}
          {selectedAssemblyIds?.length > 1 ? selectedAssemblyIds?.length : ''}{' '}
          Backflow Test
          {selectedAssemblyIds?.length > 1 ? 's' : ''}
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddJobModal.propTypes = {
  onClose: PropTypes.any,
};

export default AddJobModal;
